import {
  Alert, Anchor, Badge, Button, Card, Code, Grid, Group, Stack, Table, Tabs, Text,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft, IconDownload, IconTrash,
} from '@tabler/icons-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useDeleteProfile, useProfileDetail } from '../api/hooks';
import { getAccessToken, getCurrentProject } from '../api/client';
import { DataState } from '../components/DataState';
import { ErrorCodeBadge, StatusBadge } from '../components/StatusBadge';
import { PageHeader } from '../components/PageHeader';
import { CHANNEL_LABEL, fmtDate, fmtRelative } from '../theme/format';

export function ProfileDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useProfileDetail(id);
  const remove = useDeleteProfile();

  /**
   * Export goes through fetch rather than a plain link: the endpoint needs the
   * Authorization header and the project header, neither of which an <a href>
   * can carry.
   */
  async function exportProfile() {
    const res = await fetch(`/v1/profiles/${id}/export`, {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
        'X-Seidhi-Project': getCurrentProject() ?? '',
      },
    });
    if (!res.ok) {
      notifications.show({ color: 'red', message: 'Export failed' });
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `profile-${id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <PageHeader
        title={data?.profile.external_id ?? 'Profile'}
        description={data?.profile.email ?? undefined}
        action={
          <Group gap="xs">
            <Anchor component={Link} to="/profiles" size="sm">
              <Group gap={4}><IconArrowLeft size={14} /> All profiles</Group>
            </Anchor>
            <Button
              size="xs" variant="light" leftSection={<IconDownload size={14} />}
              onClick={exportProfile}
            >
              Export
            </Button>
            <Button
              size="xs" variant="subtle" color="red" leftSection={<IconTrash size={14} />}
              onClick={() =>
                modals.openConfirmModal({
                  title: 'Erase this person?',
                  children: (
                    <Stack gap="xs">
                      <Text size="sm">
                        The profile, its devices, and its preferences are hard-deleted.
                      </Text>
                      <Text size="sm" c="dimmed">
                        Delivery rows are kept but stripped of every identifier, so last
                        quarter&apos;s reporting still adds up. The deletion itself is
                        recorded in the audit log.
                      </Text>
                    </Stack>
                  ),
                  labels: { confirm: 'Erase', cancel: 'Cancel' },
                  confirmProps: { color: 'red' },
                  onConfirm: () =>
                    remove.mutate(id, {
                      onSuccess: () => {
                        notifications.show({ message: 'Profile erased', color: 'teal' });
                        navigate('/profiles');
                      },
                    }),
                })
              }
            >
              Erase
            </Button>
          </Group>
        }
      />

      <DataState loading={isLoading} error={error}>
        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Stack>
              <Card>
                <Text fw={500} mb="sm">Identity</Text>
                <Stack gap={6}>
                  <Field label="External ID" value={data?.profile.external_id} />
                  <Field label="Email" value={data?.profile.email} />
                  <Field label="Phone" value={data?.profile.phone} />
                  <Field label="Timezone" value={data?.profile.timezone} />
                  <Field label="First seen" value={fmtDate(data?.profile.first_seen_at)} />
                  <Field label="Last seen" value={fmtRelative(data?.profile.last_seen_at)} />
                </Stack>
              </Card>

              <Card>
                <Text fw={500} mb="sm">Traits</Text>
                <Code block style={{ fontSize: 12 }}>
                  {JSON.stringify(data?.profile.traits ?? {}, null, 2)}
                </Code>
              </Card>

              <Card>
                <Text fw={500} mb="sm">Consent</Text>
                {!data?.preferences.length ? (
                  <Alert variant="light" color="gray">
                    <Text size="sm">
                      No explicit preferences. Absence means opted in — the send gate and
                      the preference center agree on that, so this person receives
                      marketing until they say otherwise.
                    </Text>
                  </Alert>
                ) : (
                  <Table>
                    <Table.Tbody>
                      {data.preferences.map((p) => (
                        <Table.Tr key={`${p.category}-${p.channel}`}>
                          <Table.Td>
                            <Text size="sm">{p.category} · {p.channel}</Text>
                          </Table.Td>
                          <Table.Td ta="right">
                            <Badge color={p.opted_in ? 'teal' : 'red'}>
                              {p.opted_in ? 'in' : 'out'}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </Card>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 8 }}>
            <Card padding={0}>
              <Tabs defaultValue="deliveries">
                <Tabs.List>
                  <Tabs.Tab value="deliveries">Deliveries</Tabs.Tab>
                  <Tabs.Tab value="events">Events</Tabs.Tab>
                  <Tabs.Tab value="devices">Devices</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="deliveries" p="sm">
                  <Table.ScrollContainer minWidth={560}>
                    <Table>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>When</Table.Th>
                          <Table.Th>Channel</Table.Th>
                          <Table.Th>Status</Table.Th>
                          <Table.Th>Reason</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {data?.deliveries.map((d) => (
                          <Table.Tr key={d.id}>
                            <Table.Td>
                              <Text size="xs" c="dimmed">
                                {fmtRelative(d.accepted_at ?? d.delivered_at)}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Badge variant="light" size="sm">
                                {CHANNEL_LABEL[d.channel] ?? d.channel}
                              </Badge>
                            </Table.Td>
                            <Table.Td><StatusBadge status={d.status} /></Table.Td>
                            <Table.Td>
                              <ErrorCodeBadge code={d.error_code} detail={d.error_detail} />
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                </Tabs.Panel>

                <Tabs.Panel value="events" p="sm">
                  <Table>
                    <Table.Tbody>
                      {data?.events.map((e, i) => (
                        <Table.Tr key={i}>
                          <Table.Td>
                            <Text size="sm" ff="monospace">{e.name}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Code style={{ fontSize: 11 }}>
                              {JSON.stringify(e.props).slice(0, 70)}
                            </Code>
                          </Table.Td>
                          <Table.Td ta="right">
                            <Text size="xs" c="dimmed">{fmtRelative(e.timestamp)}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Tabs.Panel>

                <Tabs.Panel value="devices" p="sm">
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Platform</Table.Th>
                        <Table.Th>Token</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th>Last seen</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {data?.devices.map((d) => (
                        <Table.Tr key={d.id}>
                          <Table.Td>
                            <Badge variant="light" size="sm">{d.platform}</Badge>
                          </Table.Td>
                          <Table.Td><Code style={{ fontSize: 11 }}>{d.token}</Code></Table.Td>
                          <Table.Td><StatusBadge status={d.status} /></Table.Td>
                          <Table.Td>
                            <Text size="xs" c="dimmed">{fmtRelative(d.last_seen_at)}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                  <Text size="xs" c="dimmed" mt="sm">
                    Tokens are masked. A dead token is marked automatically the first
                    time a provider reports it gone.
                  </Text>
                </Tabs.Panel>
              </Tabs>
            </Card>
          </Grid.Col>
        </Grid>
      </DataState>
    </>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <Group justify="space-between" wrap="nowrap">
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="sm" ta="right" style={{ wordBreak: 'break-all' }}>{value || '—'}</Text>
    </Group>
  );
}
